import * as S from '@radix-ui/react-switch';
import { type ReactNode, useId } from 'react';

import { busyButton } from '../components/busy.ts';
import './ui.css';

/**
 * A bare on/off (Radix Switch), the printed switch (ui.css `.sw`): name it with `label` or
 * `labelledBy`. While `busy` it stays focusable and says so, as SwitchRow and busyButton do
 * (components/busy.ts). For a row in Settings, use `SwitchRow`.
 */
export function Switch({
	checked,
	onCheckedChange,
	label,
	labelledBy,
	describedBy,
	disabled,
	busy = false,
	working = false,
	className,
	testId,
}: {
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	label?: string | undefined;
	labelledBy?: string | undefined;
	describedBy?: string | undefined;
	disabled?: boolean | undefined;
	/** Something's in flight: it stays focusable and says so (`aria-disabled`), and a press does nothing for now. */
	busy?: boolean | undefined;
	/** This switch's own change is the one in flight (`aria-busy`). */
	working?: boolean | undefined;
	className?: string | undefined;
	testId?: string | undefined;
}) {
	return (
		<S.Root
			className={className ? `sw ${className}` : 'sw'}
			checked={checked}
			// Radix flips the state on a click; a press while busy is stopped before it.
			onClick={(e) => {
				if (busy) e.preventDefault();
			}}
			onCheckedChange={onCheckedChange}
			disabled={disabled}
			aria-disabled={busy || undefined}
			aria-busy={working || undefined}
			aria-label={label}
			aria-labelledby={labelledBy}
			aria-describedby={describedBy}
			data-testid={testId}
		>
			<S.Thumb className="sw-k" />
		</S.Root>
	);
}

/**
 * One on/off row in Settings (and Privacy's "Count my plays"): its label, a quieter line
 * under it, and the printed switch. The whole row is the switch (Radix Switch). The label
 * is its name and the line its description, read after the state, so a screen reader says
 * "Haptics, switch, on" before the detail and voice control can say "tap Haptics" (A11Y-15,
 * UX-081). While `busy` it stays focusable and says so (components/busy.ts).
 */
export function SwitchRow({
	label,
	sub,
	checked,
	onToggle,
	busy = false,
	working = false,
	testId,
}: {
	label: ReactNode;
	sub?: ReactNode;
	checked: boolean;
	onToggle: () => void;
	/** Something's in flight: a press does nothing for now. */
	busy?: boolean;
	/** This row's own change is the one in flight. */
	working?: boolean;
	testId?: string | undefined;
}) {
	const id = useId();
	const press = busyButton(busy, onToggle);
	return (
		<S.Root
			className="mn-row set"
			checked={checked}
			// Radix flips the state on a click; a press while busy is stopped before it.
			onClick={press.onClick}
			onCheckedChange={() => {}}
			aria-labelledby={`${id}-l`}
			aria-describedby={sub ? `${id}-s` : undefined}
			aria-disabled={press['aria-disabled']}
			aria-busy={working || undefined}
			data-testid={testId}
		>
			<span className="mn-lb">
				<span id={`${id}-l`}>{label}</span>
				{sub ? <small id={`${id}-s`}>{sub}</small> : null}
			</span>
			<span className="sw" aria-hidden="true">
				<S.Thumb className="sw-k" />
			</span>
		</S.Root>
	);
}
